import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { emailTemplateService, CreateTemplateRequest } from '../../services/emailTemplateService';

const EmailTemplateEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Redirect to templates list if no ID provided (no create functionality)
  useEffect(() => {
    if (!id) {
      navigate('/admin/email-templates');
    }
  }, [id, navigate]);

  const [template, setTemplate] = useState<CreateTemplateRequest>({
    template_name: '',
    subject: '',
    subject_ar: '',
    body_text: '',
    body_text_ar: '',
    body_html: '',
    body_html_ar: '',
    variables: [],
    is_active: true
  });

  const [activeLanguage, setActiveLanguage] = useState<'en' | 'ar'>('en');
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      loadTemplate(parseInt(id));
    }
  }, [id]);

  const loadTemplate = async (templateId: number) => {
    try {
      setLoading(true);
      const data = await emailTemplateService.getTemplate(templateId);
      setTemplate({
        template_name: data.template_name,
        subject: data.subject,
        subject_ar: data.subject_ar || '',
        body_text: data.body_text,
        body_text_ar: data.body_text_ar || '',
        body_html: data.body_html,
        body_html_ar: data.body_html_ar || '',
        variables: data.variables || [],
        is_active: data.is_active
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);

      if (id) {
        await emailTemplateService.updateTemplate(parseInt(id), template);
        navigate('/admin/email-templates');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };


  const replaceVariables = (content: string) => {
    let result = content;
    if (template.variables) {
      template.variables.forEach(variable => {
        const placeholder = new RegExp(`{{${variable}}}`, 'g');
        result = result.replace(placeholder, `[${variable.toUpperCase()}]`);
      });
    }
    return result;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-96">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout fullWidth={true}>
      <div className="w-full space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Email Template</h1>
            <p className="text-gray-600 mt-1">
              Edit email notification template content and preview changes
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/email-templates')}
            className="text-gray-600 hover:text-gray-800 transition-colors"
          >
            ← Back to Templates
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Template Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name
                </label>
                <input
                  type="text"
                  value={template.template_name}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                />
              </div>

              <div className="flex items-center">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <div className="mt-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={template.is_active}
                      onChange={(e) => setTemplate({ ...template, is_active: e.target.checked })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Available Variables */}
            {template.variables && template.variables.length > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Available Variables (Click to insert)
                </label>
                <div className="flex flex-wrap gap-2">
                  {template.variables.map((variable) => (
                    <span key={variable} className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-sm">
                      {`{{${variable}}}`}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Use these variables in your subject line and HTML content
                </p>
              </div>
            )}
          </div>

          {/* Language Toggle */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Template Content</h2>
              <div className="flex space-x-2">
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setActiveLanguage('en')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeLanguage === 'en'
                        ? 'bg-white text-gray-900 shadow'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    English
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveLanguage('ar')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeLanguage === 'ar'
                        ? 'bg-white text-gray-900 shadow'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    العربية
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    showPreview
                      ? 'bg-primary-100 text-primary-700 border border-primary-300'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>
            </div>

            {/* English Content */}
            {activeLanguage === 'en' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject (English) *
                  </label>
                  <input
                    type="text"
                    value={template.subject}
                    onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Email subject in English"
                    required
                  />
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    HTML Content (English) *
                  </label>
                  <textarea
                    value={template.body_html}
                    onChange={(e) => setTemplate({ ...template, body_html: e.target.value })}
                    rows={15}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm resize-y"
                    placeholder="HTML version of the email content..."
                    style={{ minHeight: '300px' }}
                    required
                  />
                </div>
              </div>
            )}

            {/* Arabic Content */}
            {activeLanguage === 'ar' && (
              <div className="space-y-4" dir="rtl">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject (Arabic)
                  </label>
                  <input
                    type="text"
                    value={template.subject_ar}
                    onChange={(e) => setTemplate({ ...template, subject_ar: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="عنوان البريد الإلكتروني باللغة العربية"
                    dir="rtl"
                  />
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    HTML Content (Arabic)
                  </label>
                  <textarea
                    value={template.body_html_ar}
                    onChange={(e) => setTemplate({ ...template, body_html_ar: e.target.value })}
                    rows={15}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm resize-y"
                    placeholder="محتوى البريد الإلكتروني HTML باللغة العربية..."
                    style={{ minHeight: '300px' }}
                    dir="ltr"
                  />
                </div>
              </div>
            )}

            {/* Preview Section */}
            {showPreview && (
              <div className="mt-6 border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Preview</h3>

                <div className="space-y-4">
                  {/* Subject Preview */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject Preview:
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg border text-sm" dir={activeLanguage === 'ar' ? 'rtl' : 'ltr'}>
                      {activeLanguage === 'ar'
                        ? (template.subject_ar ? replaceVariables(template.subject_ar) : replaceVariables(template.subject))
                        : replaceVariables(template.subject)
                      }
                    </div>
                  </div>

                  {/* HTML Preview */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      HTML Content Preview:
                    </label>
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <iframe
                        className="w-full min-h-[350px] max-h-[500px] border-0"
                        style={{
                          fontSize: '14px',
                          lineHeight: '1.5'
                        }}
                        srcDoc={`
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <meta charset="UTF-8">
                            <meta name="viewport" content="width=device-width, initial-scale=1.0">
                            <style>
                              body {
                                margin: 0;
                                padding: 16px;
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                                font-size: 14px;
                                line-height: 1.5;
                                color: #333;
                                background: #ffffff;
                                direction: ${activeLanguage === 'ar' ? 'rtl' : 'ltr'};
                              }
                              * {
                                box-sizing: border-box;
                              }
                              .no-content {
                                text-align: center;
                                color: #6b7280;
                                font-style: italic;
                                padding: 48px 0;
                              }
                            </style>
                          </head>
                          <body>
                            ${
                              activeLanguage === 'ar'
                                ? (template.body_html_ar ? replaceVariables(template.body_html_ar) : replaceVariables(template.body_html))
                                : replaceVariables(template.body_html)
                              || '<div class="no-content">No HTML content available for preview</div>'
                            }
                          </body>
                          </html>
                        `}
                        title="Email Template Preview"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/admin/email-templates')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving && <LoadingSpinner size="sm" className="mr-2" />}
              Update Template
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default EmailTemplateEditor;